import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "~/api/provider";
import {
  type SubscriptionKind,
  type SubscriptionsPage,
  type SubscriptionStatus,
  subscribeKeys,
} from "./subscribeTypes";

/**
 * Is the viewer subscribed to this thing? One question, one row, cached under the target so a
 * bell rendered twice on a page asks once.
 *
 * `enabled` lets a caller mount the hook before it knows the id (a scene whose route param is
 * still resolving) without firing a request for an empty target.
 */
export function useSubscriptionStatus(
  kind: SubscriptionKind,
  targetId: string | null | undefined,
) {
  const api = useApi();
  return useQuery({
    queryKey: subscribeKeys.status(kind, targetId ?? ""),
    enabled: Boolean(targetId),
    // A viewer with no session, or a target they cannot read, is "not subscribed" — not an
    // error worth retrying or a red state worth showing.
    retry: false,
    queryFn: async () =>
      ((await api.research.post("subscriptions.status", { kind, targetId })) as {
        data: SubscriptionStatus;
      }).data,
  });
}

/** The bell's write. Invalidates both the target's own row and the panel's list. */
export function useToggleSubscription(kind: SubscriptionKind, targetId: string | null | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subscribed: boolean) =>
      ((await api.research.post("subscriptions.toggle", { kind, targetId, subscribed })) as {
        data: SubscriptionStatus;
      }).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: subscribeKeys.status(kind, targetId ?? "") });
      void queryClient.invalidateQueries({ queryKey: subscribeKeys.mine });
    },
  });
}

/** Everything the viewer follows — the panel's read. */
export function useMySubscriptions() {
  const api = useApi();
  return useQuery({
    queryKey: subscribeKeys.mine,
    retry: false,
    queryFn: async () =>
      (await api.research.post("subscriptions.mine", { limit: 200 })) as SubscriptionsPage,
  });
}

/** Unsubscribe from the panel, where the row is known by (kind, target) rather than by bell. */
export function useUnsubscribe() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (row: { kind: SubscriptionKind; targetId: string; event: string }) =>
      api.research.post("subscriptions.toggle", {
        kind: row.kind,
        targetId: row.targetId,
        event: row.event,
        subscribed: false,
      }),
    onSuccess: (_result, row) => {
      void queryClient.invalidateQueries({ queryKey: subscribeKeys.mine });
      void queryClient.invalidateQueries({
        queryKey: subscribeKeys.status(row.kind, row.targetId),
      });
    },
  });
}
